import React, { memo, useEffect, useMemo } from "react";
import styles from "./appHeader.module.scss";
import { Button } from "../Common/Button";
import { Bug, Cog, LayoutTemplate } from "lucide-react";
import { SettingsDrawer } from "./SettingsDrawer";
import { Header } from "../Common/Header";
import { Divider } from "../Common/Divider";
import { useNavigate } from "@tanstack/react-router";
import { useAppHeader } from "../../contexts/AppHeaderContext";
import { Link } from "../Common/Link";
import { useAuth } from "../../contexts/AuthContext";
import { useGroupId } from "../../hooks/useGroupId";
import classnames from "classnames/bind";
import { useIFrame } from "contexts/IframeContext";
import { usePage } from "hooks/usePage";
import { editLayoutButtonId, settingsButtonId } from "constants/portalIds";
import { IFrameAppHeader } from "./IFrameAppHeader";
import { useKeyupListener } from "hooks/listeners/useKeyupListener";
import { findIndex } from "lodash";
import { getWrap } from "lib/arrayUtils";
import { HoverableButton } from "components/Common/HoverableButton";
import { ScaleTransition } from "components/Common/Transition/ScaleTransition";
import { usePageConfig } from "hooks/usePageConfig";
import { characterRouteAppHeaderConfig } from "components/Characters/constants/characterRouteAppHeaderConfig";
import { shipRouteAppHeaderConfig } from "components/Ships/constants/shipRouteAppHeaderConfig";
import { groupRouteAppHeaderConfig } from "components/Groups/constants/groupRouteAppHeaderConfig";
import { gameMasterAppHeaderConfig } from "components/GameMasters/constants/gameMasterAppHeaderConfig";
import { compendiumAppHeaderConfig } from "components/Compendium/consts/compendiumAppHeaderConfig";
import { useEditLayout } from "contexts/EditLayoutContext";
import { Tooltip } from "components/Common/Tooltip/Tooltip";

const classNameBuilder = classnames.bind(styles);

const appHeaderConfigs = [
  groupRouteAppHeaderConfig,
  characterRouteAppHeaderConfig,
  shipRouteAppHeaderConfig,
  gameMasterAppHeaderConfig,
  // compendiumAppHeaderConfig,
];

export const AppHeader = memo(() => {
  const { user, shouldSetupUser } = useAuth();
  const groupId = useGroupId();
  const [base] = usePage();
  const {
    portalRef,
    isPortalReady,
    setIsPortalReady,
    settingsDrawer,
    shouldDisableNav,
  } = useAppHeader();
  const { portalProps: settingsDrawerPortalProps, ...settingsDrawerProps } =
    settingsDrawer;
  const { setIsPortalComponentRendered: setIsSettingDrawerRendered } =
    settingsDrawerPortalProps;

  const { isIFrame } = useIFrame();
  const navigate = useNavigate();

  const [shouldHideHeaderPortal, shouldHideTitle] = usePageConfig([
    "shouldHideHeaderPortal",
    "shouldHideTitle",
  ]);
  const { setEditing, editingVisible } = useEditLayout();

  const setRef = (ref) => {
    if (!isPortalReady) {
      setIsPortalReady(true);
    }
    portalRef.current = ref;
  };
  const shouldShowButtons = Boolean(user) && !shouldSetupUser;

  const pageEntries = useMemo(() => {
    const base = appHeaderConfigs.filter(
      (config) => !config.staticData?.shouldHideNavButton
    );

    // if there's NOT a groupId, filter the buttons that require a group
    if (!groupId) {
      return base.filter((config) => !config.staticData?.requiresGroup);
    }

    return base;
  }, [groupId]);

  useEffect(() => {
    if (!shouldShowButtons) {
      setIsPortalReady(false);
    }
  }, [shouldShowButtons, setIsPortalReady]);

  useKeyupListener(
    (e) => {
      if (
        (e.code === "ArrowLeft" || e.code === "ArrowRight") &&
        e.altKey &&
        e.shiftKey
      ) {
        const curIndex = findIndex(
          pageEntries,
          (config) => config?.path === base
        );

        if (curIndex !== -1) {
          const newPage = getWrap(
            pageEntries,
            curIndex,
            e.code === "ArrowLeft" ? -1 : 1
          );

          newPage?.path && navigate({ to: `/${newPage?.path}` });
        }
      }
    },
    [navigate, base, pageEntries]
  );

  const headerPortal = (
    <div className={styles["portal"]} id="header-portal" ref={setRef} />
  );

  if (isIFrame) {
    return <IFrameAppHeader headerPortal={headerPortal} />;
  }

  let title = "PICO";
  let subtitle = "[BETA]";

  if (shouldHideTitle) {
    title = "";
    subtitle = "";
  }

  return (
    <Header
      title={title}
      subtitle={subtitle}
      titleIcon={<Bug />}
      titleIconClassName={styles["title-icon"]}
      className={styles["header"]}
      titleContainerClassName={styles["title-container"]}
      titleClassName={
        classNameBuilder("title", { "not-signed-in": !shouldShowButtons })
      }
      subtitleClassName={
        classNameBuilder("subtitle", { "not-signed-in": !shouldShowButtons })
      }
    >
      {shouldShowButtons ? (
        <div className={classNameBuilder("buttons")}>
          {!shouldHideHeaderPortal ? (
            <>
              {headerPortal}
              <Divider
                className={classNameBuilder("divider", "hide-when-small")}
                white
              />
            </>
          ) : null}
          <div className={styles["page-entries"]}>
            {pageEntries.map((config) => {
              const { path, staticData = {} } = config;
              const { getHeaderPath, icon: Icon, label, buttonId } = staticData;
              const selected = base === path;
              const linkParams = getHeaderPath ? getHeaderPath() : {};

              return (
                <Link key={path} to={`/${path}`} {...linkParams}>
                  <HoverableButton
                    id={buttonId}
                    dark
                    icon
                    large
                    whiteText
                    title={label}
                    selected={selected}
                    className={styles["page-button"]}
                    disableHover={selected}
                    disabled={shouldDisableNav}
                  >
                    {({ hovered: hoveredProp }) => {
                      const hovered = hoveredProp && !selected;
                      return (
                        <ScaleTransition isScaled={hovered}>
                          <Icon hovered={hovered} />
                        </ScaleTransition>
                      );
                    }}
                  </HoverableButton>
                </Link>
              );
            })}
            {pageEntries.length > 0 && (
              <Divider className={styles["divider"]} white />
            )}
            <Button
              id={editLayoutButtonId}
              icon
              large
              whiteText
              onClick={(e) => setEditing((prev) => !prev)}
              disabled={!editingVisible}
              title={"Refresh"}
            >
              <LayoutTemplate />
              <Tooltip position={"bottom-end"}>Edit Layout</Tooltip>
            </Button>
            <Button
              id={settingsButtonId}
              dark
              icon
              large
              rotation
              onClick={() => setIsSettingDrawerRendered(true)}
              title={"Settings"}
              disabled={shouldDisableNav}
            >
              <Cog />
            </Button>
          </div>
          <div className={styles["helper-buttons"]}></div>
          <SettingsDrawer
            portalProps={settingsDrawerPortalProps}
            {...settingsDrawerProps}
          />
        </div>
      ) : null}
    </Header>
  );
});
